import { useMemo } from "react";
import { apiClient } from "@bfc-software/bfc-web-ui/lib/task-planner-api-client";
import {
  pageQuery,
  type PageSearch,
} from "@bfc-software/bfc-web-ui/lib/cursor-pagination";

import {
  TaskActivationStatus,
  taskInternalIdParam,
  toTask,
  toTaskPage,
} from "./wire";
import { hasSearch } from "./task-model";
import { TaskScope } from "./filters";
import type { WirePagination, WireTaskActivationStatus } from "./wire";
import type { TaskFilters } from "./filters";
import type { QueryLike } from "./async";
import type { TaskPage } from "./types";

/**
 * The All tasks tab's only way to reach its data.
 *
 * `GET /{orgId}/task-activations`, and therefore the floor rather than the
 * warehouse: an activation listing can only see a task somebody released, so a
 * task in a plan nobody has activated does not appear on this tab at all. The
 * route says so on screen rather than leaving a manager to notice; closing it
 * needs a warehouse-scoped task listing, not another param.
 *
 * Every control narrows on the wire, and all of them are params on the single
 * request. The chip row is `status` - the `Unassigned` chip among them, which is
 * `status=pending` - the task box is `taskInternalIds`, and the route box is
 * `routeCode`, matched against the routes each task actually picks from rather
 * than against its plan's, so a merged plan's tasks narrow individually. The
 * selector is `selectorAppUserExtIds`, which takes the AAA id
 * (`external_user_id`) the column draws rather than the `selector_profile.id`
 * ULID - so the box's value reaches the wire as typed, with no roster lookup in
 * between.
 *
 * Nothing is filtered on this side, which is what makes what is on screen the
 * answer to the request rather than the answer minus whatever the page then hid.
 *
 * Read-only: nothing on this tab writes. The one write on this screen is plan
 * activation, which belongs to the other tab and lives in `use-plans.ts` - and
 * which invalidates this endpoint's key prefix, because releasing a plan is
 * exactly what puts rows here.
 */

/**
 * Rows per page.
 *
 * Held here rather than shared, because the server derives `page_number` and
 * `page_count` from the limit of the request that produced them - so the limit
 * is part of what a page ordinal means. Not user-adjustable: a page size in the
 * URL would renumber pages under the reader mid-walk.
 */
const PAGE_SIZE = 25;

/**
 * The wire statuses each chip asks for. `all` asks for none, so it sends none.
 *
 * The value type is the generated union, so the members below are held to the
 * schema by the annotation rather than by anyone checking - which is also what
 * makes a status the service renames a compile error here.
 */
const SCOPE_STATUSES: Record<
  TaskScope,
  ReadonlyArray<WireTaskActivationStatus> | undefined
> = {
  [TaskScope.ALL]: undefined,
  [TaskScope.COMPLETED]: [TaskActivationStatus.completed],
  /**
   * Held by somebody and not finished.
   *
   * Both of the statuses that mean a task has a name on it: `assigned` is one a
   * selector has been given, `claimed` one they have picked up. A chip that
   * asked for only the second reads as "in progress" while leaving out the work
   * that has been handed out and not yet started - which is exactly the half a
   * manager is chasing.
   */
  [TaskScope.PROGRESS]: [
    TaskActivationStatus.assigned,
    TaskActivationStatus.claimed,
  ],
  [TaskScope.UNASSIGNED]: [TaskActivationStatus.pending],
  /**
   * Everything released and unfinished.
   *
   * Enumerated rather than defined by exclusion, because a request can only
   * name the statuses it wants. The trade is that a status the service adds
   * later is left out of this view until this line names it - which the
   * generated type makes visible, since the schema's `status` enum is closed and
   * `TaskActivationStatus` is keyed by it, so a new member is a compile error
   * there rather than a silent omission here.
   */
  [TaskScope.OPEN]: [
    TaskActivationStatus.pending,
    TaskActivationStatus.assigned,
    TaskActivationStatus.claimed,
  ],
};

/**
 * The chip row, as the listing's `status` param.
 *
 * One control over one param. The selector box used to be a second one - it
 * carried an "unassigned" arm that was also `status=pending`, so the two had to
 * intersect, and asking for the pool inside "Completed" was a question with no
 * answer that this had to return an empty array for. The pool is a chip now, so
 * there is one control and no empty case: `undefined` means no status param at
 * all, which is every status.
 *
 * A search widens to every status, on both tabs and for the same reason
 * (`hasSearch`): a task found nowhere because it was in a scope nobody was
 * looking at is indistinguishable from a task that does not exist.
 */
export function statusQuery(
  scope: TaskScope,
  filters: TaskFilters,
): ReadonlyArray<WireTaskActivationStatus> | undefined {
  return hasSearch(filters) ? undefined : SCOPE_STATUSES[scope];
}

/**
 * The activation listing's query params, filters included.
 *
 * Pure, and exported, because the request shape is the part worth testing: a
 * filter that quietly fails to reach the query reads on screen exactly like a
 * filter that matched everything.
 *
 * Text in the task box that is not a task number at all leaves the param off
 * entirely, which reads as unfiltered - so `useTaskList` never asks in that case
 * (see `noMatches`). Stated there rather than here, because this function
 * describes a request and that case is the absence of one.
 */
export function activationsQuery({
  warehouseId,
  filters,
  scope,
  page,
}: {
  warehouseId: string;
  filters: TaskFilters;
  scope: TaskScope;
  page: PageSearch;
}) {
  const status = statusQuery(scope, filters);
  const taskId =
    filters.task.kind === "query"
      ? taskInternalIdParam(filters.task.text)
      : null;

  return {
    warehouseExtId: warehouseId,
    // Live rows only: what is held now, not the assignment history.
    live: true,
    limit: PAGE_SIZE,
    ...(status ? { status: [...status] } : {}),
    // The warehouse-scoped number a manager reads off a sheet, which is exactly
    // what the box collects - an exact-id lookup rather than a substring match.
    // Its sibling `taskIds` takes origin-task ULIDs, which nobody types.
    ...(taskId === null ? {} : { taskInternalIds: [taskId] }),
    // The AAA id the Selector column draws, straight through: this param takes
    // `external_user_id`, not the `selector_profile.id` ULID that the row's own
    // `selector_id` field and `POST .../activate`'s body carry.
    ...(filters.selector.kind === "named"
      ? { selectorAppUserExtIds: [filters.selector.name] }
      : {}),
    // Task-scoped and a substring match, which is exactly what the box collects.
    // It narrows to the routes a task's own picks reference, so a merged plan
    // answers per task rather than all-or-nothing.
    ...(filters.route.kind === "query"
      ? { routeCode: filters.route.text }
      : {}),
    ...pageQuery(page),
  };
}

/** What one page of this tab is. */
export interface TasksRead {
  page: TaskPage;
  /** The envelope, straight through, because the pager reads all of it. */
  pagination: WirePagination;
}

export type TaskListResult = QueryLike<TasksRead> & { isFetching: boolean };

/** An answer nothing can match, stated here rather than asked of the service. */
const EMPTY_READ: TasksRead = {
  page: { tasks: [], nextCursor: null, totalCount: 0 },
  pagination: {
    has_more: false,
    total_count: 0,
    page_count: 0,
    page_number: 0,
  },
};

/**
 * The tab's read: one request, with every control on it.
 *
 * One thing still resolves to "nothing can match" without being asked, and it is
 * text in the task box that names no task: `taskInternalIds` is an integer set
 * on the wire, and an omitted param reads as unfiltered, so the request that
 * would otherwise be made is the one that answers with the whole floor.
 *
 * A selector id the warehouse does not hold is not that case. The service
 * documents an unknown id as contributing nothing rather than erroring, so it
 * comes back as an empty page - which is the honest answer, and one this side no
 * longer has to anticipate.
 *
 * `orgId` is resolved from the auth session, so it can be absent for the first
 * render or two while a session is being minted; reading with no org would hit
 * `/undefined/task-activations`, so the queries wait on it instead.
 */
export function useTaskList({
  orgId,
  warehouseId,
  filters,
  scope,
  page,
}: {
  orgId: string | null;
  warehouseId: string;
  filters: TaskFilters;
  scope: TaskScope;
  page: PageSearch;
}): TaskListResult {
  const client = useMemo(() => apiClient(), []);
  const ready = typeof orgId === "string";

  /**
   * Text in the task box that is not a task number names no task.
   *
   * `taskInternalIds` is an integer set on the wire, so there is nothing to send
   * for "TASK-4001" - and sending nothing reads as unfiltered, which would answer
   * a search that matched nothing with the whole floor.
   */
  const isTaskUnaskable =
    filters.task.kind === "query" &&
    taskInternalIdParam(filters.task.text) === null;

  const noMatches = isTaskUnaskable;

  const activations = client.useQuery(
    "get",
    "/{orgId}/task-activations",
    {
      params: {
        path: { orgId: orgId ?? "" },
        query: activationsQuery({ warehouseId, filters, scope, page }),
      },
    },
    {
      enabled: ready && !noMatches,
      select: (data): TasksRead => ({
        page: toTaskPage(
          data.pagination,
          data.task_activations.map((row) =>
            toTask({ kind: "activated", row }),
          ),
        ),
        pagination: data.pagination,
      }),
    },
  );

  if (noMatches) {
    return {
      isPending: false,
      isError: false,
      error: null,
      data: EMPTY_READ,
      isFetching: false,
    };
  }

  return { ...activations, isFetching: activations.isFetching };
}
